import { WEEKDAY_LABELS } from '@crm/shared';
import { cn, Label, Modal, Select } from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormField } from '@/components';
import { useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { useServices } from '@/modules/catalog/api';
import { type CashbackRule, useCreateCashbackRule, useUpdateCashbackRule } from '../api';

const FORM_ID = 'cashback-rule-form';

const ruleFormSchema = z
  .object({
    name: z.string().min(2, 'Informe o nome').max(80),
    percent: z.coerce.number().int().min(1, 'Informe o percentual').max(100),
    serviceId: z.string(),
    unitId: z.string(),
    weekdays: z.array(z.coerce.number().int()),
    startTime: z.string(),
    endTime: z.string(),
    validityDays: z.coerce.number().int().min(1, 'Informe a validade').max(3650),
    minAmount: z.coerce.number().nonnegative(),
  })
  .refine((values) => !values.startTime || !values.endTime || values.endTime > values.startTime, {
    message: 'O fim precisa ser depois do inicio',
    path: ['endTime'],
  });

type RuleFormValues = z.infer<typeof ruleFormSchema>;

/** "13:30" vira 810 minutos; e assim que a regra guarda a faixa de horario. */
function toMinutes(value: string): number | undefined {
  if (!value) return undefined;

  const [hours, minutes] = value.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

function toTimeInput(minutes: number | null): string {
  if (minutes === null) return '';

  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

interface RuleDialogProps {
  open: boolean;
  rule?: CashbackRule | null;
  onClose: () => void;
}

/** Cadastro da regra: quanto volta, em que servico, dia e horario. */
export function RuleDialog({ open, rule, onClose }: RuleDialogProps) {
  const { user } = useSession();
  const units = user?.units ?? [];
  const services = useServices({ page: 1, perPage: 100 });

  const createRule = useCreateCashbackRule();
  const updateRule = useUpdateCashbackRule();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RuleFormValues>({
    resolver: zodResolver(ruleFormSchema),
    defaultValues: {
      name: '',
      percent: 5,
      serviceId: '',
      unitId: '',
      weekdays: [],
      startTime: '',
      endTime: '',
      validityDays: 90,
      minAmount: 0,
    },
  });

  const weekdays = watch('weekdays');

  useEffect(() => {
    if (!open) return;

    reset({
      name: rule?.name ?? '',
      percent: rule?.percent ?? 5,
      serviceId: rule?.serviceId ?? '',
      unitId: rule?.unitId ?? '',
      weekdays: rule?.weekdays ?? [],
      startTime: toTimeInput(rule?.startMinutes ?? null),
      endTime: toTimeInput(rule?.endMinutes ?? null),
      validityDays: rule?.validityDays ?? 90,
      minAmount: rule?.minAmount ?? 0,
    });
  }, [open, rule, reset]);

  function toggleWeekday(day: number) {
    const current = weekdays ?? [];
    setValue(
      'weekdays',
      current.includes(day) ? current.filter((entry) => entry !== day) : [...current, day].sort(),
    );
  }

  function onSubmit(values: RuleFormValues) {
    const input = {
      name: values.name,
      percent: values.percent,
      serviceId: values.serviceId || undefined,
      unitId: values.unitId || undefined,
      weekdays: values.weekdays,
      startMinutes: toMinutes(values.startTime),
      endMinutes: toMinutes(values.endTime),
      validityDays: values.validityDays,
      minAmount: values.minAmount || undefined,
      isActive: rule?.isActive ?? true,
    };

    if (rule) {
      updateRule.mutate({ id: rule.id, input }, { onSuccess: onClose });
      return;
    }

    createRule.mutate(input, { onSuccess: onClose });
  }

  const error = createRule.error ?? updateRule.error;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={rule ? 'Editar regra' : 'Nova regra de cashback'}
      size="medium"
      actions={[
        { label: 'Cancelar', variant: 'ghost', onClick: onClose },
        {
          label: rule ? 'Salvar' : 'Criar',
          type: 'submit',
          formId: FORM_ID,
          loading: createRule.isPending || updateRule.isPending,
        },
      ]}
    >
      <form id={FORM_ID} className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
        {error ? <p className="text-sm text-destructive">{toErrorMessage(error)}</p> : null}

        <FormField
          label="Nome da regra"
          placeholder="Terca e quarta com 10%"
          error={errors.name?.message}
          {...register('name')}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            label="Cashback (%)"
            type="number"
            min="1"
            max="100"
            error={errors.percent?.message}
            {...register('percent')}
          />
          <FormField
            label="Validade (dias)"
            type="number"
            min="1"
            error={errors.validityDays?.message}
            {...register('validityDays')}
          />
          <FormField
            label="Valor minimo (R$)"
            type="number"
            step="0.01"
            min="0"
            error={errors.minAmount?.message}
            {...register('minAmount')}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="serviceId">Servico</Label>
            <Select id="serviceId" {...register('serviceId')}>
              <option value="">Todos os servicos</option>
              {(services.data?.data ?? []).map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </Select>
          </div>

          {units.length > 1 ? (
            <div className="space-y-2">
              <Label htmlFor="unitId">Unidade</Label>
              <Select id="unitId" {...register('unitId')}>
                <option value="">Todas as unidades</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="weekdays">Dias da semana</Label>
          {/* Nenhum marcado = vale todos os dias. */}
          <div id="weekdays" className="flex flex-wrap gap-1.5">
            {WEEKDAY_LABELS.map((label, day) => (
              <button
                key={label}
                type="button"
                onClick={() => toggleWeekday(day)}
                className={cn(
                  'h-10 w-12 rounded-md border text-sm transition-colors',
                  (weekdays ?? []).includes(day)
                    ? 'border-primary bg-primary/10 font-medium text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Nenhum dia marcado = vale todos os dias.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="A partir das"
            type="time"
            error={errors.startTime?.message}
            {...register('startTime')}
          />
          <FormField
            label="Ate as"
            type="time"
            error={errors.endTime?.message}
            {...register('endTime')}
          />
        </div>
      </form>
    </Modal>
  );
}
